/**
 * L'écran du personnage, en trois volets comme dans le jeu :
 * caractéristiques, équipement, métiers.
 */
import type { Ctx } from "../contexte.ts"
import { sousOnglets } from "../contexte.ts"
import { libelleActivite } from "./jeu.ts"
import { esc, nb, jauge, orJauge, vert, feuille } from "../fmt.ts"
import { icone, cleClasse, ic } from "../sprites.ts"
import { item } from "../../sim/contenu.ts"
import { xpRequise, detailDegats, ELEMENTS } from "../../sim/combat.ts"
import { SLOTS, LIBELLE_SLOT, perfection, panoplies, bonusEquipement, type Slot } from "../../sim/objets.ts"
import { lignesJet } from "../jet.ts"
import { forgeable } from "./forge.ts"
import { panneauCoffre } from "./sac.ts"
import { voletPanoplie, panoplieDeLObjet } from "./panoplie.ts"
import { niveauMetier, xpMetier, xpRequiseMetier } from "../../sim/recolte.ts"
import { CHASSEUR, bonusChasse, terrainsDeChasse } from "../../sim/chasse.ts"
import { classe, coutSlot } from "../../sim/classes.ts"
import { pseudoLocal } from "../../net/compte.ts"
import { TOUS_DOFUS } from "../../sim/dofus.ts"

export const SOUS_PERSO: [string, string][] = [
  ["carac", "Caractéristiques"], ["equip", "Équipement"], ["metiers", "Métiers"],
  ["compte", "Compte"],
]

export function vuePerso(c: Ctx): string {
  const { p } = c
  if (!p) return ""
  const sous = SOUS_PERSO.some(([k]) => k === c.sous) ? c.sous : "carac"
  // La fiche ne s'affiche que sur « Caractéristiques » : partout ailleurs elle
  // mangeait le haut de l'écran sans rien apprendre.
  return `${sousOnglets(sous, SOUS_PERSO)}${
    sous === "equip" ? voletEquipement(c)
    : sous === "metiers" ? voletMetiers(c)
    : sous === "compte" ? voletCompte(c)
    : enteteJoueur(c) + voletCarac(c)}`
}

/**
 * Sécuriser sa partie, ou en retrouver une.
 *
 * Sans compte, la partie ne vit que dans ce navigateur : un nettoyage et des
 * mois de jeu disparaissent. On ne l'impose pas — jouer reste possible en trois
 * secondes — mais on le dit clairement dès que le joueur a de quoi perdre.
 */
/** L'adresse publique d'un joueur. */
export const lienPartage = (pseudo: string) =>
  `${location.origin}/j/${encodeURIComponent(pseudo)}`

function voletCompte(c: Ctx): string {
  const pseudo = pseudoLocal()
  if (pseudo) {
    return `
    <section class="carte">
      <h2>Compte</h2>
      <p class="secur ok">Partie sécurisée sous le pseudonyme <b>${esc(pseudo)}</b>.</p>
      <p class="aide">Tu la retrouveras sur n'importe quel appareil en te connectant avec ce pseudonyme et ton mot de passe.</p>
      <div class="actions"><button id="deconnexion">Se déconnecter de cet appareil</button></div>
      <p class="aide">La partie n'est pas supprimée : elle reste sur le serveur, tu peux revenir quand tu veux.</p>
    </section>

    <section class="carte partageCarte">
      <h2>Partager mon profil</h2>
      <p class="aide">Une page publique montre ton niveau, tes succès et ton équipe.
        Le lien s'affiche avec une image de ton profil sur Twitter, Discord ou WhatsApp.</p>
      <p class="lienPartage"><code id="v-lienpartage">${esc(lienPartage(pseudo))}</code></p>
      <div class="actions">
        <button class="primaire" id="partager">${c.partage.envoi ? "Préparation…" : "Copier le lien"}</button>
        <a class="mini" id="voirPartage" href="${esc(lienPartage(pseudo))}" target="_blank" rel="noopener">Voir la page</a>
      </div>
      ${c.partage.message ? `<p class="verdict ${c.partage.erreur ? "ko" : "ok"}">${esc(c.partage.message)}</p>` : ""}
      <p class="aide">L'image se refait toute seule quand tu joues : elle suit ta progression sans que tu aies à repartager.</p>
    </section>
    ${mentions()}`
  }
  return `
  <section class="carte">
    <h2>Sécuriser ma partie</h2>
    <p class="secur alerte">Ta partie n'existe que dans ce navigateur. Un nettoyage, un changement de téléphone, et elle est perdue.</p>
    <p class="aide">Choisis un pseudonyme et un mot de passe : c'est tout. Aucune adresse de courriel n'est demandée, et le mot de passe n'est jamais conservé en clair.</p>
    <form id="formSecuriser" class="formcompte" autocomplete="on">
      <input name="pseudo" type="text" placeholder="Pseudonyme" autocomplete="username" minlength="3" maxlength="20" required />
      <input name="motdepasse" type="password" placeholder="Mot de passe (8 caractères minimum)" autocomplete="new-password" minlength="8" required />
      <button class="primaire" type="submit">Sécuriser</button>
    </form>
    <p class="msg" id="msgSecuriser"></p>
  </section>

  <section class="carte">
    <h2>J'ai déjà une partie</h2>
    <p class="aide">Connecte-toi pour la récupérer ici. <b>Attention :</b> la partie en cours sur cet appareil sera remplacée.</p>
    <form id="formConnexion" class="formcompte" autocomplete="on">
      <input name="pseudo" type="text" placeholder="Pseudonyme" autocomplete="username" required />
      <input name="motdepasse" type="password" placeholder="Mot de passe" autocomplete="current-password" required />
      <button type="submit">Récupérer ma partie</button>
    </form>
    <p class="msg" id="msgConnexion"></p>
  </section>
  ${mentions()}`
}

/**
 * Les mentions, à portée du joueur.
 *
 * Elles vivent surtout sur la vitrine, mais un joueur arrivé par un lien direct
 * ne la voit jamais : elles doivent aussi être atteignables depuis le jeu.
 */
const mentions = () => `
  <section class="carte">
    <h2>À propos</h2>
    <p class="aide"><b>Dofidle est un projet de fan, amateur et non officiel</b>, sans aucun lien avec Ankama Games.
    <em>Dofus</em>, son univers et l'ensemble des éléments qui en sont issus sont la propriété exclusive d'Ankama Games.</p>
    <p class="aide">Le jeu est entièrement gratuit : aucune publicité, aucun achat, aucun don, aucun revenu d'aucune sorte.</p>
    <div class="actions"><a class="lienplat" href="/#mentions">Mentions complètes et droits</a></div>
  </section>`

function enteteJoueur(c: Ctx): string {
  const { etat, p, s, contenu } = c
  if (!p || !s) return ""
  const cout = coutSlot(etat.personnages.length)
  return `
  <section class="carte fiche">
    <div class="entete">
      <div class="avatar" style="${icone("classes", cleClasse(p.classe), 56)}"></div>
      <div class="ident">
        <h2>${esc(p.nom)}</h2>
        <p class="aide">${esc(classe(p.classe).nom)} · niveau ${p.niveau}</p>
      </div>
      <div class="kills"><span id="v-kills">${nb(p.kills)}</span><span>victoires</span></div>
    </div>
    <div class="ligneXp"><span>Expérience</span><b id="v-xp">${nb(p.xp)} / ${nb(xpRequise(p.niveau))}</b></div>
    ${jauge(p.xp / xpRequise(p.niveau), orJauge, "j-xp")}
    <div class="ligneXp"><span>${ic("vitalite", 18)}Points de vie</span><b id="v-pv">${Math.round(p.pv)} / ${Math.round(s.pvMax)}</b></div>
    ${jauge(p.pv / s.pvMax, vert, "j-pv")}
    ${etat.personnages.length > 1 ? `
      <div class="equipe">${etat.personnages.map((x) => `
        <button class="membre ${x.id === p.id ? "actif" : ""}" data-perso="${x.id}">
          <span class="tete" style="${icone("classes", cleClasse(x.classe), 32)}"></span>
          <span class="txt">
            <span class="nom">${esc(x.nom)} <em>niv ${x.niveau}</em></span>
            <span class="act">${x.activite ? "⚔ " : ""}${esc(libelleActivite(x, contenu))}</span>
          </span>
        </button>`).join("")}</div>` : ""}
    ${cout !== null ? `<button class="discret" id="recruter" ${etat.kamas >= cout ? "" : "disabled"}>+ Recruter un personnage · ${nb(cout)} k</button>` : ""}
  </section>`
}

const CARACS: [keyof NonNullable<Ctx["p"]>["carac"], string, string][] = [
  ["vitalite", "Vitalité", "vitalite"], ["force", "Force", "terre"],
  ["intelligence", "Intelligence", "feu"], ["chance", "Chance", "eau"],
  ["agilite", "Agilité", "air"], ["sagesse", "Sagesse", "sagesse"],
]

/** Les pas de répartition proposés, du plus prudent au tout-en-un. */
const PAS: [string, string][] = [["1", "+1"], ["5", "+5"], ["10", "+10"], ["tout", "tout"]]

function voletCarac(c: Ctx): string {
  const { p, s, contenu } = c
  if (!p || !s) return ""
  // La part de l'équipement, bonus de panoplie compris : sans elle on lit un
  // total sans savoir d'où il vient.
  const b = bonusEquipement(p.equipement, contenu)
  // La réserve pilote toute la carte : sans point à placer, les pas et les
  // boutons n'ont plus lieu d'être et la liste redevient une simple fiche.
  const libre = p.pointsLibres
  return `
  <section class="carte">
    <h2>Caractéristiques <em id="v-points">${libre > 0 ? `${nb(libre)} points` : ""}</em></h2>
    ${libre > 0 ? `<div class="pas" role="group" aria-label="Combien de points placer à chaque fois">
      ${PAS.map(([v, lib]) => `<button data-pas="${v}" aria-current="${String(v) === String(c.pas)}"
        ${v !== "tout" && libre < Number(v) ? "disabled" : ""}>${lib}</button>`).join("")}
    </div>` : ""}
    <div class="caracs">${CARACS.map(([k, lib, icn]) => {
      const eq = b[k]
      // Tout sur une ligne : « 115 + 109 = 224 ». Le total isolé à droite, avec
      // le détail renvoyé en dessous, se lisait comme deux chiffres sans rapport.
      return `<div class="carac">
        <span>${ic(icn, 22)}${lib}</span>
        <em class="calcul" data-calc="${k}">${eq !== 0 ? `${nb(p.carac[k])} <span>+ ${nb(eq)}</span> =` : ""}</em>
        <b data-v="${k}">${nb(p.carac[k] + eq)}</b>
        ${libre > 0 ? `<button class="ajout" data-carac="${k}" data-combien="${c.pas}"
          aria-label="Placer ${c.pas === "tout" ? "tous les points" : c.pas + " points"} en ${lib}">+</button>` : ""}
      </div>`
    }).join("")}</div>
    <p class="aide">Les points que tu as répartis, plus <span class="apport">l'apport de l'équipement</span> et des panoplies, égalent le total.</p>
    <div class="actions"><button id="resetCarac" ${Object.values(p.carac).every((v) => v === 0) ? "disabled" : ""}>Tout remettre à zéro</button></div>
  </section>

  <section class="carte">
    <h2>En combat</h2>
    <p class="aide">Une part de tes coups frappe en <b>critique</b>, une part de
    ceux que tu reçois est <b>esquivée</b>, et l'<b>entame</b> est le temps, au
    début de chaque combat, où l'adversaire ne riposte pas encore — précieuse sur
    les combats courts, négligeable sur un boss.</p>
    <p class="aide">Ce que ces chiffres valent une fois en jeu. Les quatre premiers
    sont des <b>dégâts par seconde</b>, pas des caractéristiques. Contre chaque
    monstre tu frappes avec <b>celui qui porte le mieux</b> : ses résistances
    peuvent te faire changer d'élément, et c'est pour ça qu'elles s'affichent
    sous l'arène.</p>
    ${formuleDegats(c)}
    <div class="chiffres">
      <div class="chiffre"><span>${ic("terre", 18)}Dégâts Terre</span><b>${nb(s.degats.terre)} <em>/s</em></b></div>
      <div class="chiffre"><span>${ic("feu", 18)}Dégâts Feu</span><b>${nb(s.degats.feu)} <em>/s</em></b></div>
      <div class="chiffre"><span>${ic("eau", 18)}Dégâts Eau</span><b>${nb(s.degats.eau)} <em>/s</em></b></div>
      <div class="chiffre"><span>${ic("air", 18)}Dégâts Air</span><b>${nb(s.degats.air)} <em>/s</em></b></div>
      <div class="chiffre"><span>${ic("critique", 18)}Critique</span><b>${Math.round(s.critique)} <em>%</em></b></div>
      <div class="chiffre"><span>${ic("fuite", 18)}Esquive</span><b>${Math.round(s.esquive * 100)} <em>%</em></b></div>
      <div class="chiffre"><span>${ic("initiative", 18)}Entame</span><b>${s.entameS.toFixed(2)} <em>s</em></b></div>
      <div class="chiffre"><span>${ic("invocation", 18)}Invocations</span><b>+${Math.round(s.invocation * 100)} <em>%</em></b></div>
      <div class="chiffre"><span>${ic("pa", 18)}Cadence</span><b>${Math.round(s.vitesse * 100)} <em>%</em></b></div>
      <div class="chiffre"><span>${ic("pm", 18)}PA / PM</span><b>${s.pa} <em>/ ${s.pm}</em></b></div>
      <div class="chiffre"><span>${ic("prospection", 18)}Prospection</span><b>${nb(s.prospection)}</b></div>
      <div class="chiffre"><span>${ic("resistance", 18)}Résistance</span><b>${nb(s.resistance)} %</b></div>
      <div class="chiffre"><span>${ic("sagesse", 18)}Sagesse</span><b>${nb(s.sagesse)} %</b></div>
      <div class="chiffre"><span>${ic("soin", 18)}Soins reçus</span><b>${Math.round(s.soin * 100)} %</b></div>
    </div>
    ${voletSecondaires(b)}
  </section>`
}

/**
 * Les statistiques secondaires apportées par l'équipement.
 *
 * Puissance et Dommages entrent bien dans le calcul des dégâts — un point de
 * Puissance vaut un point de caractéristique, un point de Dommages en vaut
 * trois — mais rien ne les affichait : les dégâts montaient sans qu'on puisse
 * dire d'où ça venait.
 */
function voletSecondaires(b: ReturnType<typeof bonusEquipement>): string {
  const lignes: [string, string, number, string][] = [
    ["puissance", "Puissance", b.puissance, "vaut un point de caractéristique"],
    ["critique", "% Critique", b.critique, "part de tes coups qui frappent en critique"],
    ["critique", "Dommages Critiques", b.domCritique, "ce qu'un coup critique ajoute"],
    ["fuite", "Fuite", b.fuite, "alimente l'esquive"],
    ["tacle", "Tacle", b.tacle, "alimente l'esquive"],
    ["initiative", "Initiative", b.initiative, "allonge l'entame du combat"],
    ["po", "Portée", b.portee, "allonge l'entame du combat"],
    ["invocation", "Invocation", b.invocation, "une créature se bat à tes côtés"],
    ["pod", "Pod", b.pods, "agrandit le coffre de l'équipe"],
    ["pa", "PA", b.pa, "accélère tes frappes d'un sixième chacun"],
    ["pm", "PM", b.pm, "accélère ta récolte d'un tiers chacun"],
    ["terre", "Dommages Terre", b.dommages.terre, "chaque point vaut 3 dégâts"],
    ["feu", "Dommages Feu", b.dommages.feu, "chaque point vaut 3 dégâts"],
    ["eau", "Dommages Eau", b.dommages.eau, "chaque point vaut 3 dégâts"],
    ["air", "Dommages Air", b.dommages.air, "chaque point vaut 3 dégâts"],
    ["soin", "Soin", b.soin, "augmente ce que rendent les vivres"],
    ["terre", "Résistance Terre", b.resistances.terre, "réduit les dégâts subis"],
    ["feu", "Résistance Feu", b.resistances.feu, "réduit les dégâts subis"],
    ["eau", "Résistance Eau", b.resistances.eau, "réduit les dégâts subis"],
    ["air", "Résistance Air", b.resistances.air, "réduit les dégâts subis"],
  ]
  const utiles = lignes.filter(([, , v]) => v !== 0)
  return `
  <div class="secondaires">
    <p class="titrelot">Apport de l'équipement</p>
    ${utiles.length === 0
      ? `<p class="aide">Ton équipement n'apporte encore ni Puissance ni Dommages. Ce sont eux qui font décoller les dégâts, bien plus que les caractéristiques seules.</p>`
      : `<ul class="jetl">${utiles.map(([icn, lib, v, quoi]) => `
        <li>
          <span class="v">${v > 0 ? "+" : ""}${nb(v)}</span>
          <span class="n">${ic(icn, 16)}${esc(lib)}</span>
          <em>${esc(quoi)}</em>
        </li>`).join("")}</ul>`}
  </div>`
}

/**
 * Le calcul des dégâts, écrit en clair pour l'élément dominant.
 *
 * « 265 » à côté d'une Force de 224 n'apprend rien : le socle, le facteur de
 * classe et le poids triple des points de Dommages sont invisibles. Les nombres
 * viennent de `detailDegats`, celui-là même qu'emploie le moteur.
 */
function formuleDegats(c: Ctx): string {
  const { p, contenu } = c
  if (!p) return ""
  const el = [...ELEMENTS].sort((a, b) =>
    detailDegats(p, b, contenu).total - detailDegats(p, a, contenu).total)[0]!
  const d = detailDegats(p, el, contenu)
  const cl = classe(p.classe)
  const pts = d.carac + d.equip + d.puissance
  const morceaux = [
    `${nb(d.carac)}${d.equip ? ` + ${nb(d.equip)}` : ""}${d.puissance ? ` + ${nb(d.puissance)} <span class="pt2">Puissance</span>` : ""}`,
  ]
  return `<details class="formule">
    <summary>D'où viennent ces ${nb(d.total)} dégâts ${esc(el)} ?</summary>
    <ul>
      <li><b>${nb(pts)}</b> points d'élément : ${morceaux.join("")}</li>
      <li>chaque point vaut <b>0,9</b> dégât ${d.dommages ? `, chaque point de Dommages en vaut <b>3</b>` : ""}</li>
      <li>socle de <b>${d.socle}</b> pour un personnage nu</li>
      ${d.facteur !== 1 ? `<li>multiplié par <b>${d.facteur.toLocaleString("fr-FR")}</b>, bonus ${esc(cl.nom)} (${esc(cl.systeme)})</li>` : ""}
    </ul>
    <p class="calculfinal">(${d.socle} + ${nb(pts)} × 0,9${
      d.dommages ? ` + ${nb(d.dommages)} × 3` : ""})${
      d.facteur !== 1 ? ` × ${d.facteur.toLocaleString("fr-FR")}` : ""} = <b>${nb(d.total)}</b> par seconde</p>
  </details>`
}

function slotHtml(c: Ctx, sl: Slot): string {
  const p = c.p!
  const e = p.equipement[sl]
  const it = e ? item(c.contenu, e.item) : null
  const choisi = e && c.pieceChoisie === e.uid
  // Un clic **sélectionne** : il affiche le jet. Retirer est une action à part :
  // toucher une pièce pour la voir ne doit pas la déséquiper.
  return `<button class="slot ${e ? "plein" : ""} ${choisi ? "choisi" : ""}" ${e ? `data-piece="${e.uid}"` : ""}
          title="${it ? esc(it.nom) : LIBELLE_SLOT[sl]}">
    ${it ? `<span class="ic" style="${icone("items", it.id, 38)}"></span>` : `<span class="lb">${LIBELLE_SLOT[sl]}</span>`}
    ${it && e ? `<span class="pf">${perfection(it, e)}%</span>` : ""}
  </button>`
}

/**
 * Ce que fait un Dofus, et ce qu'il reste à accomplir pour l'obtenir.
 * Contrairement à l'équipement, un Dofus n'a pas de jet : il est acquis ou non,
 * et son effet vaut pour **tout le compte** (voir `effetsDofus`).
 */
function detailDofus(c: Ctx): string {
  const { etat, contenu } = c
  const d = TOUS_DOFUS.find((x) => x.id === c.dofusChoisi)
  if (!d) return `<p class="aide">Touche une pièce pour voir son jet.</p>`
  const eu = etat.dofus.includes(d.id)
  const pr = d.progres(etat, contenu)
  const it = item(contenu, d.item)
  const pct = pr.requis > 0 ? pr.actuel / pr.requis : 0

  const effets: string[] = []
  const mult = (v: number | undefined) => `+${Math.round(((v ?? 1) - 1) * 100)} %`
  if (d.effet.degats) effets.push(`${ic("puissance", 16)}Dégâts ${mult(d.effet.degats)}`)
  if (d.effet.recolte) effets.push(`${ic("metier-1", 16)}Récolte ${mult(d.effet.recolte)}`)
  if (d.effet.vitesse) effets.push(`${ic("initiative", 16)}Cadence ${mult(d.effet.vitesse)}`)
  if (d.effet.regen) effets.push(`${ic("vitalite", 16)}Régénération ${mult(d.effet.regen)}`)
  if (d.effet.prospection) effets.push(`${ic("prospection", 16)}Prospection +${d.effet.prospection}`)
  if (d.effet.doubleJet) effets.push(`${ic("critique", 16)}Chaque craft est tiré deux fois, le meilleur jet est gardé`)

  return feuille(`<div class="detail dofusdetail">
    <div class="entete">
      <span class="ic" style="${icone("items", d.item, 48) || `background:${d.couleur};border-radius:50%`}"></span>
      <div class="ident">
        <b>${esc(it?.nom ?? d.nom)}</b>
        <span>${eu ? `<b class="pct">obtenu</b>` : "à mériter"} · met à l'épreuve ${esc(d.systeme)}</span>
      </div>
    </div>
    <p class="cond">${esc(d.condition.charAt(0).toUpperCase() + d.condition.slice(1))}.</p>
    ${eu ? "" : `<div class="ligneXp"><span>Progression</span><b>${nb(pr.actuel)} / ${nb(pr.requis)}</b></div>
      ${jauge(pct, pct >= 1 ? vert : orJauge)}`}
    <ul class="effetsdofus">${effets.map((x) => `<li>${x}</li>`).join("")}</ul>
    <p class="aide">Son effet vaut pour tout le compte, sur chaque personnage.</p>
  </div>`)
}

/**
 * Le détail de la pièce **portée** : son jet, et de quoi la ranger.
 *
 * Les pièces du coffre sont traitées par le panneau partagé, sous la grille ;
 * sans cette séparation les deux panneaux affichaient la même pièce en double,
 * avec deux boutons « Équiper ».
 */
function detailPiece(c: Ctx): string {
  const { contenu, p } = c
  if (!p || !c.pieceChoisie) {
    return `<p class="aide">Touche une pièce pour voir son jet.</p>`
  }
  const porteEq = Object.entries(p.equipement).find(([, v]) => v?.uid === c.pieceChoisie)
  const porte = porteEq?.[1]
  const it = porte ? item(contenu, porte.item) : null
  if (!porte || !it) return `<p class="aide">Touche une pièce pour voir son jet.</p>`

  const pct = perfection(it, porte)
  return feuille(`<div class="detail">
    <div class="entete">
      <span class="ic" style="${icone("items", it.id, 48)}"></span>
      <div class="ident">
        <b>${esc(it.nom)}</b>
        <span>${esc(it.type || "—")} · niv ${it.niveau} · <b class="pct">${pct} % parfait</b></span>
      </div>
    </div>
    ${lignesJet(contenu, porte)}
    ${panoplieDeLObjet(c, it.id)}
    <div class="actions">
      ${forgeable(contenu, porte)
        ? `<button class="primaire" data-ouvrirforge="${porte.uid}">Forgemagie</button>` : ""}
      <button data-retirer="${porteEq![0]}">Ranger au coffre</button>
    </div>
  </div>`)
}

function voletEquipement(c: Ctx): string {
  const { etat, contenu, p } = c
  if (!p) return ""
  const actives = [...panoplies(p.equipement, contenu)].filter(([, n]) => n >= 2)

  return `
  <section class="carte">
    <h2>Équipement</h2>
    <div class="poupee">
      <div class="col">${(["coiffe", "amulette", "anneau1", "ceinture"] as Slot[]).map((sl) => slotHtml(c, sl)).join("")}</div>
      <div class="portrait"><span style="background-image:url(/img/combat/c${p.classe}-0.webp)"></span></div>
      <div class="col">${(["cape", "arme", "bouclier", "anneau2"] as Slot[]).map((sl) => slotHtml(c, sl)).join("")}</div>
    </div>
    <div class="basse">${(["bottes", "familier"] as Slot[]).map((sl) => slotHtml(c, sl)).join("")}</div>
    <p class="capDofus">Dofus <b>${TOUS_DOFUS.filter((d) => etat.dofus.includes(d.id)).length} / ${TOUS_DOFUS.length}</b></p>
    <div class="dofusslots">${TOUS_DOFUS.map((d) => {
      const eu = etat.dofus.includes(d.id)
      return `<button class="slot dofus ${eu ? "plein" : ""} ${c.dofusChoisi === d.id ? "choisi" : ""}"
        data-dofus="${d.id}" title="${esc(d.nom)}">${
        eu ? `<span class="ic" style="${icone("items", d.item, 28) || `background:${d.couleur}`}"></span>`
           : `<span class="pastille" style="background:${d.couleur}"></span>`}</button>`
    }).join("")}</div>
    ${c.dofusChoisi ? detailDofus(c) : detailPiece(c)}
  </section>

  ${actives.length ? `<section class="carte">
    <h2>Panoplies</h2>
    ${actives.map(([id, n]) => voletPanoplie(c, id, n)).join("")}
  </section>` : ""}

  ${panneauCoffre(c)}`
}


function voletMetiers(c: Ctx): string {
  const { contenu, p } = c
  if (!p) return ""
  return `
  <section class="carte">
    <h2>Métiers</h2>
    <div class="metiers">${Object.entries(contenu.metiers).map(([id, mj]) => {
      const m = Number(id)
      const niv = niveauMetier(p, m)
      const xp = xpMetier(p, m)
      const requis = xpRequiseMetier(niv)
      const chasse = m === CHASSEUR
      const actif = chasse
        ? p.activite?.type === "combat"
        : p.activite?.type === "recolte" && contenu.recoltes[String(p.activite.ressourceId)]?.metier === m
      return `<div class="metier ${actif ? "encours" : ""}">
        <span class="ic">${ic(`metier-${mj.icone}`, 34)}</span>
        <span class="n">${esc(mj.nom)}${actif ? " <em>en cours</em>" : ""}
          ${chasse ? `<em class="quoi">au combat</em>` : ""}</span>
        <b>${niv}</b>
        ${jauge(xp / requis, orJauge)}
      </div>`
    }).join("")}</div>
    <p class="aide">Les métiers montent <b>par personnage</b> : c'est ce qui rend un second personnage désirable. Le niveau ouvre l'accès aux ressources supérieures.</p>
    ${aideChasseur(c)}
  </section>`
}

/**
 * Le Chasseur ne se récolte pas : il faut le dire, et dire où.
 *
 * Cinq métiers se montent en choisissant un gisement dans l'écran Monde. Le
 * sixième n'apparaît nulle part dans cet écran, et rien ne l'expliquait — un
 * joueur a fini par poser la question. Un métier qu'on ne sait pas monter est un
 * métier cassé, même quand le code est juste.
 */
function aideChasseur(c: Ctx): string {
  const { contenu, p } = c
  if (!p) return ""
  const niv = niveauMetier(p, CHASSEUR)
  const bonus = Math.round(bonusChasse(niv) * 100)
  // Les familles à portée : celles qu'on peut affronter maintenant, les plus
  // giboyeuses d'abord.
  const terrains = terrainsDeChasse(contenu)
    .filter((f) => f.niveauMin <= p.niveau + 10)
    .slice(0, 6)

  return `
  <div class="chasse">
    <h3>${ic("metier-41", 20)} Le Chasseur monte en combattant</h3>
    <p class="aide">Il ne récolte rien : il dépèce. Chaque pièce de <b>viande</b> qui
      tombe d'un monstre lui donne de l'expérience, sans que tu aies à choisir
      entre te battre et récolter. Son niveau ne change pas ta cadence — elle
      appartient au combat — mais ton <b>rendement</b> :
      <b>${bonus} %</b> de chances de ramener une pièce en plus, ${
        niv >= 200 ? "le maximum" : `le double à 200`}.</p>
    ${terrains.length === 0
      ? `<p class="aide">Aucune famille giboyeuse à ta portée pour l'instant.</p>`
      : `<p class="aide">Les meilleurs terrains à ta portée :</p>
        <div class="liste">${terrains.map((f) => `
          <button class="rangee" data-famille="${f.id}">
            <span class="nom">${esc(f.nom)}</span>
            <span class="niv">niv ${f.niveauMin} · ${Math.round(f.part * 100)} % des monstres lâchent du gibier</span>
            <span class="risque ok">Chasser ›</span>
          </button>`).join("")}</div>`}
  </div>`
}

export { SLOTS }
